import '@awesome.me/webawesome/dist/styles/webawesome.css';
import '../components/breadcrumb.js';
import '../components/closable_callout.js';

import { Task, TaskStatus } from '@lit/task';
import { css, html, LitElement, nothing, type TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { loadErrorCallout } from '../components/load_error_callout.js';
import {
    createFarm,
    createOrphanFarm,
    type IFarm,
} from '../libraries/api_client/farm.js';
import {
    getOrganization,
    type IOrganization,
} from '../libraries/api_client/organization.js';
import { valueOrThrow } from '../libraries/api_result.js';
import { notifyFarmsChanged } from '../libraries/events.js';
import { FormController } from '../libraries/form_controller.js';
import { type DeclaredField } from '../libraries/form_field_types.js';
import { formHostStyles } from '../libraries/form_host_styles.js';
import { formOutcomeOf, type FormSubmitResult, formSubmitted } from '../libraries/form_outcome.js';
import {
    farmAddressOverrides,
    nameAndLegalFormFields,
    operatorRegistrationNumberField,
    readFarmData,
} from '../libraries/form_presets.js';
import { redirectTo } from '../libraries/navigation.js';
import { organizationTypeLabel } from '../libraries/organization_type.js';

const organizationUnavailableDescription = 'Die Organisation konnte nicht geladen werden.';

@customElement('fm-farm-create')
export class FmFarmCreate extends LitElement {

    static styles = [
        formHostStyles,
        css`
            :host {
                display: block;
            }

            h1 {
                margin: 0 0 var(--wa-space-l) 0;
                font-size: var(--wa-font-size-l);
                font-weight: var(--wa-font-weight-bold);
                color: var(--fm-color-text-normal);
            }

            .description {
                font-size: var(--wa-font-size-s);
                color: var(--fm-color-text-quiet);
                margin-bottom: var(--wa-space-l);
            }
        `,
    ];

    @property({
        type: String,
    })
    organizationId = '';

    /**
     * The one organisation the route names, read for the screen alone: its name and its
     * type reach the breadcrumb and the readonly field. The unscoped route names none and
     * therefore reads nothing.
     *
     * Nothing on the submitting path waits for it, so a slow or failed read costs the
     * name and nothing else. Keyed on the id, so a route pointing at another
     * organisation re-reads instead of naming the one it read before.
     */
    private readonly organizationTask = new Task(this, {
        task: async ([
            organizationId,
        ], { signal }): Promise<IOrganization | null> => {
            if (organizationId.length === 0) {
                return null;
            }

            return valueOrThrow(await getOrganization(organizationId, {
                signal,
            }));
        },
        args: (): [string] => [
            this.organizationId,
        ],
    });

    private get routeOrganization (): IOrganization | null {
        return this.organizationTask.value ?? null;
    }

    /**
     * Creates the farm, in the organisation the route names or in none.
     *
     * The axis is the server's to decide, so this page neither guesses nor asks: it posts
     * to the endpoint its own route names and reads the outcome back off the answer.
     * @param formData - The submitted values.
     * @returns What the request reported.
     */
    private async createFarmFrom (formData: FormData): Promise<FormSubmitResult> {
        const data = readFarmData(formData);
        const result = this.organizationId.length > 0 ?
            await createFarm(this.organizationId, data) :
            await createOrphanFarm(data);

        return formOutcomeOf(result, (farm): FormSubmitResult => {
            this.finishCreation(farm);

            return formSubmitted;
        });
    }

    /**
     * Announces the new farm to the scopes whose rosters now hold it and opens it.
     *
     * Both read the created farm rather than the route: an advised farm has no managing
     * organisation, so it is reached outside any organisation scope, where the Stammdaten
     * show the Betriebsleitung the creator just acquired.
     * @param farm - The created farm.
     */
    private finishCreation (farm: IFarm): void {
        notifyFarmsChanged(farm.managingOrganizationId);

        if (farm.consultingOrganizationId !== null) {
            notifyFarmsChanged(farm.consultingOrganizationId);
        }

        redirectTo(farm.managingOrganizationId === null ?
            `/farms/${farm.id}` :
            `/organizations/${farm.managingOrganizationId}/farms/${farm.id}`);
    }

    private readonly handleCancel = (): void => {
        if (this.organizationId.length > 0) {
            redirectTo(`/organizations/${this.organizationId}/farms`);
        } else {
            redirectTo('/home');
        }
    };

    private readonly handleRetry = (): void => {
        void this.organizationTask.run();
    };

    private renderOrganizationError (): TemplateResult | typeof nothing {
        return loadErrorCallout(
            this.organizationTask.status === TaskStatus.ERROR,
            organizationUnavailableDescription,
            this.handleRetry
        );
    }

    /**
     * The organisation the route names, shown rather than offered, and the only
     * organisation field there is: the unscoped route assigns none. Its label says which
     * axis the farm will land on.
     *
     * A `readonly` `wa-input` for the reason the Nutzungsart control is one
     * (`libraries/parcel_form_controls.ts`). It declares no control and submits nothing,
     * the route carrying the organisation, so there is nothing to validate or announce.
     * @returns The field, or nothing where there is no organisation to name.
     */
    private contextOrganizationField (): DeclaredField {
        const org = this.routeOrganization;

        if (org === null) {
            return false;
        }

        const label = organizationTypeLabel(org.type);

        return {
            kind: 'custom',
            key: 'contextOrganization',
            content: (): TemplateResult => html`
                <wa-input size="s" readonly label=${label} value=${org.nameWithLegalForm}></wa-input>
            `,
        };
    }

    private readonly form = new FormController(this, () => ({
        fields: [
            this.contextOrganizationField(),
            ...nameAndLegalFormFields(undefined, 'z.B. Musterbetrieb'),
            operatorRegistrationNumberField(),
            {
                kind: 'address',
                placeholders: {
                    streetName: 'z.B. Feldweg',
                    houseNumber: 'z.B. 12',
                    postalCode: 'z.B. 24103',
                    city: 'z.B. Kiel',
                },
                overrides: farmAddressOverrides,
            },
        ],
        submitLabel: 'Anlegen',
        onCancel: (): void => this.handleCancel(),
        submit: async (formData: FormData): Promise<FormSubmitResult> => this.createFarmFrom(formData),
    }));

    private renderBreadcrumb (): TemplateResult {
        if (this.organizationId.length === 0) {
            return html`
                <fm-breadcrumb .items=${[
                    {
                        label: 'Alle',
                        href: '/organizations',
                    },
                    {
                        label: 'Betrieb anlegen',
                    },
                ]}></fm-breadcrumb>
            `;
        }

        const org = this.routeOrganization;
        const orgName = org !== null ? org.nameWithLegalForm : 'Alle';

        return html`
            <fm-breadcrumb .items=${[
                {
                    label: orgName,
                    href: `/organizations/${this.organizationId}/farms`,
                },
                {
                    label: 'Betrieb anlegen',
                },
            ]}></fm-breadcrumb>
        `;
    }

    render (): TemplateResult {
        return html`
            ${this.renderBreadcrumb()}
            <h1>Betrieb anlegen</h1>
            <p class="description">
                Legen Sie einen neuen Betrieb an, um mit der Düngeplanung zu beginnen.
            </p>

            ${this.renderOrganizationError()}

            ${this.form.render()}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fm-farm-create': FmFarmCreate;
    }
}
